import { useCallback, useEffect, useMemo, useState } from 'react'
import { AssetScopeChip } from '../AssetScopeChip'
import { useAsset } from '../../hooks/useQueries'
import { matchesWithPath } from '../../lib/assetPath'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiDelete, mdiVectorPolyline } from '@mdi/js'

import { $api } from '../../api/queryClient'
import { useApi } from '../../api/hooks'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import type { components } from '../../api/schema'

type Issue = components['schemas']['Issue'] & {
    flags?: string[]
    properties?: Record<string, unknown>
}
type AnnotationGeometry = components['schemas']['AnnotationGeometry']

export interface AnnotationDraft {
    geometry: AnnotationGeometry
    page: number
}

interface BaseProps {
    org: string
    representationUid: string
    assetUid: string
    onClose: () => void
    onSaved: () => void
    onEditGeometry?: () => void
}

type Props =
    | (BaseProps & { mode: 'create'; draft: AnnotationDraft; issue?: undefined; pendingGeometry?: AnnotationGeometry })
    | (BaseProps & { mode: 'edit'; issue: Issue; draft?: undefined; pendingGeometry?: AnnotationGeometry })

export function AnnotationTaggingPanel(props: Props) {
    const { t } = useTranslation('assets')
    const { org, representationUid, mode, onClose, onSaved, onEditGeometry, pendingGeometry } = props
    const queryClient = useQueryClient()
    const { request } = useApi()

    const existingProps = useMemo(() => {
        if (mode !== 'edit') return null
        const p = (props.issue.properties as { annotation?: components['schemas']['AnnotationProperties'] } | undefined)?.annotation
        return p ?? null
    }, [mode, props])

    const initialColor = existingProps?.style?.color || '#4A90D9'

    const [label, setLabel] = useState(mode === 'edit' ? props.issue.title || '' : '')
    const [linkedAssetUid, setLinkedAssetUid] = useState<string>(existingProps?.subject_asset_uid || '')
    const [linkedAssetInitial, setLinkedAssetInitial] = useState<ApiSearchableSelectOption[]>([])
    const [color, setColor] = useState<string>(initialColor)
    const [notes, setNotes] = useState<string>(mode === 'edit' ? props.issue.description || '' : '')
    const [error, setError] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)

    // Sync state when the edited issue changes (e.g. another tab navigation).
    useEffect(() => {
        if (mode === 'edit') {
            const p = (props.issue.properties as { annotation?: components['schemas']['AnnotationProperties'] } | undefined)?.annotation
            setLabel(props.issue.title || '')
            setLinkedAssetUid(p?.subject_asset_uid || '')
            setColor(p?.style?.color || '#4A90D9')
            setNotes(props.issue.description || '')
        }
    }, [mode, props])

    // Seed the linked-asset picker with the currently-linked asset's name so it
    // shows the human label without requiring a search.
    useEffect(() => {
        const uid = existingProps?.subject_asset_uid
        if (!uid) {
            setLinkedAssetInitial([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const resp = await request(
                    '/api/v1/organizations/{orgCode}/assets/{assetUid}',
                    'GET',
                    { params: { path: { orgCode: org, assetUid: uid } } },
                )
                if (cancelled) return
                const a = resp as { uid?: string; name?: string; code?: string } | undefined
                if (a?.uid) {
                    setLinkedAssetInitial([{ value: a.uid, label: a.name || a.code || a.uid }])
                }
            } catch {
                // ignore — type-ahead still works
            }
        })()
        return () => {
            cancelled = true
        }
    }, [existingProps?.subject_asset_uid, org, request])

    // Scope the search to the plan's floor or building; the user can widen it.
    const { data: planAsset } = useAsset(org, props.assetUid)
    const [widened, setWidened] = useState(false)
    const searchAssets = useCallback(
        async (query: string): Promise<ApiSearchableSelectOption[]> => {
            try {
                const queryParams: Record<string, string> = { limit: '50', asset_chain: 'true' }
                if (!widened) queryParams.ancestor_uid = props.assetUid
                if (query) {
                    queryParams.q = query
                    queryParams.sort = 'name'
                } else {
                    queryParams.sort = '-updated_at'
                }
                const response = await request(
                    '/api/v1/organizations/{orgCode}/assets',
                    'GET',
                    { params: { path: { orgCode: org }, query: queryParams } },
                )
                const assets = (response as { data?: { uid: string; name?: string; code?: string; parent_asset_uid?: string | null; parent_chain?: boolean }[] }).data ?? []
                return matchesWithPath(assets).map(({ row, label }) => ({ value: row.uid, label: row.name ? label : row.code || label }))
            } catch {
                return []
            }
        },
        [org, request, widened, props.assetUid],
    )

    const refreshIssues = () => {
        queryClient.invalidateQueries({
            predicate: q => {
                const k = q.queryKey as unknown[]
                return k.length >= 2 && k[0] === 'get' && k[1] === '/api/v1/organizations/{orgCode}/issues'
            },
        })
    }

    const createMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/issues')
    const patchMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/issues/{issueUid}')
    const deleteMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/issues/{issueUid}')

    const buildAnnotation = (page: number, geometry: AnnotationGeometry) => ({
        page,
        geometry,
        style: { color },
        subject_asset_uid: linkedAssetUid || undefined,
    })

    const onSave = async () => {
        setError(null)
        try {
            if (mode === 'create') {
                const geometry = pendingGeometry ?? props.draft.geometry
                const annotation = buildAnnotation(props.draft.page, geometry)
                // The API client's CreateIssueRequest schema lags behind the
                // backend (target_type='asset_representation', the new
                // 'annotation' issue type, properties.annotation). Cast around
                // the generated typing.
                const body = {
                    title: label || '',
                    type: 'annotation' as const,
                    priority: 3,
                    target_type: 'asset_representation',
                    target_entity_uid: representationUid,
                    properties: { annotation },
                    description: notes || undefined,
                }
                await createMutation.mutateAsync({
                    params: { path: { orgCode: org } },
                    body: body as unknown as components['schemas']['CreateIssueRequest'],
                })
            } else {
                const oldGeom = existingProps?.geometry
                const oldPage = existingProps?.page ?? 1
                const geometry = pendingGeometry ?? oldGeom ?? { kind: 'point', points: [{ x: 0.5, y: 0.5 }] }
                const annotation = buildAnnotation(oldPage, geometry)
                await patchMutation.mutateAsync({
                    params: { path: { orgCode: org, issueUid: props.issue.uid } },
                    body: {
                        title: label || '',
                        description: notes || undefined,
                        properties: { annotation },
                    } as unknown as components['schemas']['UpdateIssueRequest'],
                })
            }
            refreshIssues()
            onSaved()
        } catch (e: unknown) {
            const msg = (e as { message?: string })?.message
            setError(msg || t('plan.annotation.save_failed', 'Save failed'))
        }
    }

    const onDelete = async () => {
        if (mode !== 'edit') return
        try {
            await deleteMutation.mutateAsync({
                params: { path: { orgCode: org, issueUid: props.issue.uid } },
            })
            refreshIssues()
            onSaved()
        } catch (e: unknown) {
            const msg = (e as { message?: string })?.message
            setError(msg || t('plan.annotation.delete_failed', 'Delete failed'))
        }
    }

    const title = mode === 'create'
        ? t('plan.annotation.create_title', 'New annotation')
        : t('plan.annotation.edit_title', 'Edit annotation')

    const saveLabel = mode === 'create'
        ? t('plan.annotation.save', 'Save annotation')
        : t('plan.annotation.save_changes', 'Save changes')
    const cancelLabel = t('plan.cancel', 'Cancel')
    const deleteLabel = t('plan.annotation.delete', 'Delete annotation')
    const editGeomLabel = t('plan.annotation.edit_shape', 'Edit shape')

    return (
        <>
            <Sheet open onOpenChange={open => { if (!open) onClose() }}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-md flex flex-col gap-4 p-6"
                    data-testid="annotation-panel"
                >
                    <SheetHeader>
                        <SheetTitle>{title}</SheetTitle>
                    </SheetHeader>

                    <div className="space-y-4 flex-1 overflow-auto">
                        <div className="space-y-1">
                            <Label htmlFor="annotation-label">{t('plan.annotation.label', 'Label')}</Label>
                            <Input
                                id="annotation-label"
                                value={label}
                                onChange={e => setLabel(e.target.value)}
                                data-testid="annotation-label-input"
                                placeholder={t('plan.annotation.label_placeholder', 'e.g. Office 204')}
                            />
                        </div>

                        <div className="space-y-1">
                            <Label>{t('plan.annotation.linked_asset', 'Linked asset')}</Label>
                            <AssetScopeChip
                                scopeLabel={planAsset?.name ?? undefined}
                                widened={widened}
                                onWiden={() => setWidened(true)}
                                testId="annotation-asset"
                            />
                            <ApiSearchableSelect
                                key={widened ? 'everywhere' : 'scoped'}
                                value={linkedAssetUid}
                                placeholder={t('plan.annotation.no_link', 'No link')}
                                searchPlaceholder={t('plan.annotation.search_asset', 'Search assets…')}
                                emptyMessage={t('plan.annotation.no_assets', 'No assets found')}
                                onValueChange={setLinkedAssetUid}
                                onSearch={searchAssets}
                                initialOptions={linkedAssetInitial}
                                debounceMs={200}
                                minSearchLength={0}
                                data-testid="annotation-asset-select"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="annotation-color">{t('plan.annotation.color', 'Color')}</Label>
                            <input
                                id="annotation-color"
                                type="color"
                                value={color}
                                onChange={e => setColor(e.target.value)}
                                className="h-9 w-16 rounded border border-input bg-transparent cursor-pointer"
                                data-testid="annotation-color-input"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="annotation-notes">{t('plan.annotation.notes', 'Notes')}</Label>
                            <Textarea
                                id="annotation-notes"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                rows={3}
                                data-testid="annotation-notes-input"
                            />
                        </div>

                        {error && <div className="text-destructive text-sm" data-testid="annotation-error">{error}</div>}
                    </div>

                    <div className="flex items-center gap-2 border-t border-border pt-3">
                        <Button
                            size="icon"
                            onClick={onSave}
                            disabled={createMutation.isPending || patchMutation.isPending}
                            aria-label={saveLabel}
                            title={saveLabel}
                            data-testid="annotation-save"
                        >
                            <AppIcon path={mdiCheck} size="md" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={onClose}
                            aria-label={cancelLabel}
                            title={cancelLabel}
                            data-testid="annotation-cancel"
                        >
                            <AppIcon path={mdiClose} size="md" />
                        </Button>
                        {mode === 'edit' && onEditGeometry && (
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={onEditGeometry}
                                aria-label={editGeomLabel}
                                title={editGeomLabel}
                                data-testid="annotation-edit-geometry"
                            >
                                <AppIcon path={mdiVectorPolyline} size="md" />
                            </Button>
                        )}
                        {mode === 'edit' && (
                            <Button
                                variant="destructive"
                                size="icon"
                                aria-label={deleteLabel}
                                title={deleteLabel}
                                onClick={() => setConfirmDelete(true)}
                                data-testid="annotation-delete"
                                className="ml-auto"
                            >
                                <AppIcon path={mdiDelete} size="md" />
                            </Button>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('plan.annotation.delete_confirm_title', 'Delete this annotation?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('plan.annotation.delete_confirm_desc', 'This cannot be undone.')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="annotation-delete-cancel">{t('plan.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="annotation-delete-confirm"
                        >
                            {t('plan.annotation.delete', 'Delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
